import React, { useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { RouterUrlParams } from 'App';
import { useScope } from 'contexts/scopeContext';
import { Program } from 'types/Program';
import { Organization } from 'types/Organization';
import { LandingPageFormsAvailability } from 'types/LandingPageFormsAvailability';
import { fixedT } from 'locale';
import { useAuth } from 'contexts/authContext';
import { ButtonClean } from 'components/_new/Button';
import { Menu, MenuItem } from 'components/_new/Menu';
import { OverlayElement } from 'components/_new/Overlay';
import { useNavigate } from 'hooks/useNavigate';
import { getBrowserLanguage } from '../../../utils/getBrowserLanguage';

interface WelcomeMessageProps {
	pageDetails: Program | Organization;
	formsAvailability?: LandingPageFormsAvailability;
}

const WelcomeMessage = ({ pageDetails, formsAvailability }: WelcomeMessageProps) => {
	const { currentUser } = useAuth();
	const { organizationName, programName } = useParams() as RouterUrlParams;
	const navigate = useNavigate();
	const menuRef = useRef<OverlayElement>(null);

	const { hasPanelAccess, acceptedApplicationsData } = useScope();

	if (!currentUser) {
		return (
			<Link to="/login" className="p-button-text font-bold underline p-0 text-pink">
				{fixedT(getBrowserLanguage() || pageDetails.language, 'auth.buttons.login')}
			</Link>
		);
	}

	const currentLanguage = currentUser.language || pageDetails.language;
	const hasUserArea = hasPanelAccess && acceptedApplicationsData?.length > 0;
	const hasAnyFormAvailable =
		formsAvailability?.isMentorRecruitmentAvailable || formsAvailability?.isMenteeRecruitmentAvailable;
	const getWelcomeMessage = (name: string) =>
		name
			? fixedT(currentLanguage, 'landingPages.welcome', { name })
			: fixedT(currentLanguage, 'landingPages.welcomeBack');

	const desktopContent = hasUserArea ? (
		<Link
			to={`/panel/${organizationName}/${programName}`}
			className="p-button-text font-bold underline p-0 text-sm text-pink"
		>
			{getWelcomeMessage(currentUser.firstName)}
		</Link>
	) : (
		<div className="p-button-text font-bold underline p-0 text-sm text-pink">
			{getWelcomeMessage(currentUser.firstName)}
		</div>
	);

	const getMobileContent = () => {
		if (hasUserArea) {
			return (
				<Link
					to={`/panel/${organizationName}/${programName}`}
					className="p-button-text font-bold underline p-0 text-sm text-pink"
				>
					{fixedT(currentLanguage, 'landingPages.buttons.userArea')}
				</Link>
			);
		}

		if (hasAnyFormAvailable) {
			const menuOptions = [
				formsAvailability?.isMentorRecruitmentAvailable && {
					key: 'mentorApplication',
					type: 'button',
					label: fixedT(currentLanguage, 'landingPages.buttons.mentorApplication'),
					onClick: () => navigate(`/${organizationName}/${programName}/apply-mentor`),
					className: 'text-sm text-white hover:text-200 hover:bg-gray-700 font-bold py-2',
				},
				formsAvailability?.isMenteeRecruitmentAvailable && {
					key: 'menteeApplication',
					type: 'button',
					label: fixedT(currentLanguage, 'landingPages.buttons.menteeApplication'),
					onClick: () => navigate(`/${organizationName}/${programName}/apply-mentee`),
					className: 'text-sm text-white hover:text-200 hover:bg-gray-700 font-bold py-2',
				},
			].filter(Boolean) as MenuItem[];

			return (
				<>
					<ButtonClean
						className="p-button-text font-bold underline p-0 text-sm text-pink"
						onClick={({ currentTarget }) => {
							menuRef?.current?.toggle(currentTarget);
						}}
					>
						{fixedT(currentLanguage, 'landingPages.buttons.joinProgram')}
						<i className="pi pi-chevron-down ml-1" style={{ fontSize: '0.75rem' }} />
					</ButtonClean>
					<Menu
						ref={menuRef}
						items={menuOptions}
						variant="bigger"
						boxVariant="clean"
						boxClassName="bg-primary py-2 ml-2 text-white hover:text-700"
					/>
				</>
			);
		}

		return (
			<div className="p-button-text font-bold underline p-0 text-sm text-pink">
				{getWelcomeMessage(currentUser.firstName)}
			</div>
		);
	};

	return (
		<>
			<div className="hidden md:block">{desktopContent}</div>
			<div className="block md:hidden">{getMobileContent()}</div>
		</>
	);
};

export default WelcomeMessage;
